import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "@/lib/api-client";
import { COMPANY_PROFILE_QUERY_KEY } from "@/hooks/api/use-company-profile";
import { COMPANIES_QUERY_KEYS } from "@/hooks/api/use-companies";

/** Trade route: prefer UN/LOCODE ports; legacy country-only pairs still supported by API */
export interface OperationalTradeRoute {
    route_id?: string;
    origin_port_locode?: string;
    destination_port_locode?: string;
    origin_country_code?: string;
    destination_country_code?: string;
    note?: string;
}

export interface OperationalProfile {
    hs_codes: string[];
    trade_routes: OperationalTradeRoute[];
    carrier_scacs: string[];
}

export const OPERATIONAL_PROFILE_QUERY_KEY = (companyId: string) => ["operational-profile", companyId] as const;

export function useOperationalProfile(companyId: string | undefined, enabled: boolean) {
    return useQuery({
        queryKey: companyId ? OPERATIONAL_PROFILE_QUERY_KEY(companyId) : ["operational-profile", "none"],
        queryFn: () => apiGet<OperationalProfile>(`/companies/${companyId}/operational-profile`),
        enabled: Boolean(companyId && enabled),
    });
}

export function useUpdateOperationalProfile(companyId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: OperationalProfile) =>
            apiPut<OperationalProfile>(`/companies/${companyId}/operational-profile`, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: OPERATIONAL_PROFILE_QUERY_KEY(companyId) });
            queryClient.invalidateQueries({ queryKey: COMPANY_PROFILE_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEYS.detail(companyId) });
        },
    });
}
